import { api } from '../lib/api-client';

/**
 * Fetch all DAS policies for the current user.
 * GET /das/policies
 */
export const getDasPolicies = async () => {
  try {
    const response = await api.get('/das/policies');
    return response.data;
  } catch (error) {
    console.error('Error fetching DAS policies:', error);
    throw error;
  }
};

/**
 * Fetch available contribution years.
 * GET /contributed/years
 */
export const getContributionYears = async () => {
  try {
    const response = await api.get('/contributed/years');
    return response.data;
  } catch (error) {
    console.error('Error fetching contribution years:', error);
    throw error;
  }
};

/**
 * Fetch full details for a single DAS policy.
 * GET /das/policy-details?policy_number=...
 */
export const getDasPolicyDetails = async (policyNumber) => {
  try {
    const response = await api.get('/das/policy-details', {
      params: { policy_number: policyNumber },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching DAS policy details:', error);
    throw error;
  }
};

/**
 * Fetch DAS transactions history.
 * GET /das/transactions?policy_number=&transaction_type=&date_from=&date_to=
 */
export const getDasTransactions = async (filters = {}) => {
  try {
    const params = {};
    if (filters.policy_number || filters.policyNumber) {
      params.policy_number = filters.policy_number || filters.policyNumber;
    }
    // Always send transaction_type (empty when not selected)
    params.transaction_type = filters.transaction_type || "";
    if (filters.date_from) {
      params.date_from = filters.date_from;
    }
    if (filters.date_to) {
      params.date_to = filters.date_to;
    }
    const response = await api.get('/das/transactions', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching DAS transactions:', error);
    throw error;
  }
};

/**
 * Fetch DAS policy statement summary.
 * GET /das/policy-statement-summary?policy_number=&statement_type=&format=
 */
export const getDasPolicyStatementSummary = async (filters = {}) => {
  try {
    const params = {};
    if (filters.policy_number || filters.policyNumber) {
      params.policy_number = filters.policy_number || filters.policyNumber;
    }
    params.statement_type = filters.statement_type || 'summary';
    params.format = filters.format || 'pdf';
    const response = await api.get('/das/policy-statement-summary', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching DAS policy statement summary:', error);
    throw error;
  }
};

/**
 * Download a DAS member certificate.
 * GET /v1/das/member-certificate?policy_number=...
 */
export const getDasMemberCertificate = async (policyNumber) => {
  const response = await api.get('/v1/das/member-certificate', {
    params: { policy_number: policyNumber },
    responseType: 'blob',
  });

  return response;
};

/**
 * Fetch the DAS fund summary (schemes + pensions counts) for the current user.
 * GET /v1/das/fund-summary  ->  { success, data: { total_schemes, total_pensions } }
 */
export const getDasFundSummary = async () => {
  try {
    const response = await api.get('/v1/das/fund-summary');
    return response.data;
  } catch (error) {
    console.error('Error fetching DAS fund summary:', error);
    throw error;
  }
};

/**
 * Download a DAS contributions statement (pre-signed PDF URL).
 * GET /das/download/contributions-statement?policy_number=&start_year=&end_year=
 */
export const getDasContributionsStatement = async (filters = {}) => {
  try {
    const params = {};
    params.policy_number = filters.policy_number || filters.policyNumber;
    params.start_year = filters.start_year ?? filters.startYear;
    params.end_year = filters.end_year ?? filters.endYear;
    const response = await api.get('/das/download/contributions-statement', { params });
    return response.data;
  } catch (error) {
    console.error('Error downloading DAS contributions statement:', error);
    throw error;
  }
};

/**
 * Fetch DAS contributions statement.
 * GET /das/contributions?policy_number=&member_number=&fund_year=&date_from=&date_to=
 */
export const getDasContributions = async (filters = {}) => {
  try {
    const params = {};
    if (filters.policy_number || filters.policyNumber) {
      params.policy_number = filters.policy_number || filters.policyNumber;
    }
    if (filters.member_number || filters.memberNumber) {
      params.member_number = filters.member_number || filters.memberNumber;
    }
    if (filters.fund_year || filters.fundYear) {
      params.fund_year = filters.fund_year || filters.fundYear;
    }
    if (filters.date_from) {
      params.date_from = filters.date_from;
    }
    if (filters.date_to) {
      params.date_to = filters.date_to;
    }
    const response = await api.get('/das/contributions', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching DAS contributions:', error);
    throw error;
  }
};
